import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../platform/api';
import { Users, MessageSquare, FileText, TrendingUp, Loader2, Mail, CheckCircle } from 'lucide-react';

interface DashboardStats {
  total_users: number;
  total_posts: number;
  total_knowledge_items: number;
  active_pipelines: number;
}

export function AdminDashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [passcodeStatus, setPasscodeStatus] = useState<'unknown' | 'checking' | 'enabled' | 'disabled' | 'enabling' | 'error'>('unknown');
  const [passcodeError, setPasscodeError] = useState<string | null>(null);

  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoading(true);
        const result = await apiRequest<{ data: DashboardStats }>('/api/admin/stats');
        setStats(result.data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load stats');
      } finally {
        setLoading(false);
      }
    };

    const checkPasscode = async () => {
      setPasscodeStatus('checking');
      try {
        const result = await apiRequest<{ data: { signIn: { methods: Array<{ identifier: string; verificationCode: boolean }> } } }>('/api/admin/logto/sign-in-experience');
        const hasEmailPasscode = result.data?.signIn?.methods?.some(
          (m: { identifier: string; verificationCode: boolean }) => m.identifier === 'email' && m.verificationCode
        );
        setPasscodeStatus(hasEmailPasscode ? 'enabled' : 'disabled');
      } catch {
        setPasscodeStatus('unknown');
      }
    };

    loadStats();
    checkPasscode();
  }, []);

  const handleEnablePasscode = async () => {
    setPasscodeStatus('enabling');
    setPasscodeError(null);
    try {
      await apiRequest('/api/admin/logto/enable-passcode', { method: 'POST' });
      setPasscodeStatus('enabled');
    } catch (err) {
      setPasscodeError(err instanceof Error ? err.message : 'Failed to enable passcode login');
      setPasscodeStatus('error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      </div>
    );
  }

  const statCards = [
    {
      label: 'Total Users',
      value: stats?.total_users || 0,
      icon: Users,
      color: 'purple',
      onClick: () => navigate('/admin/users'),
    },
    {
      label: 'Total Posts',
      value: stats?.total_posts || 0,
      icon: MessageSquare,
      color: 'green',
      onClick: () => {},
    },
    {
      label: 'Knowledge Items',
      value: stats?.total_knowledge_items || 0,
      icon: FileText,
      color: 'blue',
      onClick: () => {},
    },
    {
      label: 'Active Pipelines',
      value: stats?.active_pipelines || 0,
      icon: TrendingUp,
      color: 'orange',
      onClick: () => navigate('/admin/performance'),
    },
  ];

  return (
    <div className="p-8" style={{ fontFamily: "'Outfit', sans-serif" }}>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="text-gray-500 mt-2">System overview and quick stats</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.label}
              onClick={card.onClick}
              className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow cursor-pointer"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-500 mb-1">{card.label}</div>
                  <div className="text-3xl font-bold text-gray-900">{card.value}</div>
                </div>
                <div className={`p-3 bg-${card.color}-100 rounded-lg`}>
                  <Icon className={`h-6 w-6 text-${card.color}-600`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => navigate('/admin/users/create')}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Users className="h-4 w-4" />
            Invite User
          </button>
          <button
            onClick={() => navigate('/admin/prompts')}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            <MessageSquare className="h-4 w-4" />
            Manage Prompts
          </button>
          <button
            onClick={() => navigate('/admin/performance')}
            className="flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <TrendingUp className="h-4 w-4" />
            View Performance
          </button>
        </div>
      </div>

      {/* Auth Setup */}
      <div className="mt-8 bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Authentication Setup</h2>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mail className="h-5 w-5 text-gray-500" />
            <div>
              <div className="font-medium text-gray-900">Email Verification Code Sign-in</div>
              <div className="text-sm text-gray-500">
                Users sign in with a verification code sent to their email — no passwords needed.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {passcodeStatus === 'enabled' && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                <CheckCircle className="h-4 w-4" />
                Enabled
              </span>
            )}
            {passcodeStatus === 'disabled' && (
              <button
                onClick={handleEnablePasscode}
                className="px-4 py-2 bg-mazo-600 text-white text-sm font-medium rounded-lg hover:bg-mazo-700 transition-colors"
              >
                Enable
              </button>
            )}
            {(passcodeStatus === 'checking' || passcodeStatus === 'enabling') && (
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            )}
            {passcodeStatus === 'error' && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-red-600">{passcodeError}</span>
                <button
                  onClick={handleEnablePasscode}
                  className="px-3 py-1.5 text-sm text-red-700 border border-red-300 rounded-lg hover:bg-red-50"
                >
                  Retry
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* System Info */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <FileText className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <div className="font-medium text-blue-900">Admin Panel</div>
            <div className="text-sm text-blue-700 mt-1">
              Manage users, configure AI models, monitor performance, and control system settings.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboardPage;
